import { useState, useRef, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import API from "@/lib/api";
import { Send, Loader2, CheckCircle, AlertCircle, ArrowRight, Sparkles } from "lucide-react";
import { toast } from "sonner";

export function ClarifyStep({ projectId, concept, onConceptChange, onComplete }) {
  const [analyzing, setAnalyzing] = useState(false);
  const [analysis, setAnalysis] = useState(null); // { strengths, gaps }
  const [questions, setQuestions] = useState([]); // [{id, question, why}]
  const [answers, setAnswers] = useState({}); // { q1: "answer", q2: "..." }
  const [conversation, setConversation] = useState([]); // [{role, text}]
  const [processing, setProcessing] = useState(false);
  const [enrichedConcept, setEnrichedConcept] = useState("");
  const [followUps, setFollowUps] = useState([]);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollIntoView({ behavior: "smooth" });
  }, [questions, followUps, enrichedConcept]);

  const handleAnalyze = async () => {
    if (!concept.trim()) return;
    setAnalyzing(true);
    setAnalysis(null);
    setQuestions([]);
    setAnswers({});
    setConversation([]);
    setEnrichedConcept("");
    setFollowUps([]);
    try {
      const { data } = await API.post("/prompts/clarify", { concept: concept.trim(), project_id: projectId });
      setAnalysis(data.analysis || { strengths: [], gaps: [] });
      setQuestions(data.questions || []);
    } catch (e) {
      toast.error("Analysis failed", { description: e.response?.data?.detail || "Try again." });
    } finally {
      setAnalyzing(false);
    }
  };

  const handleSubmitAnswers = async () => {
    const answered = questions.filter((q) => answers[q.id]?.trim());
    if (answered.length === 0 && followUps.filter((q) => answers[q.id]?.trim()).length === 0) return;
    setProcessing(true);

    // Build conversation from current Q&A
    const newMessages = [];
    const allQuestions = [...questions, ...followUps];
    allQuestions.forEach((q) => {
      if (answers[q.id]) {
        newMessages.push({ role: "ai", text: q.question });
        newMessages.push({ role: "user", text: answers[q.id] });
      }
    });
    const fullConversation = [...conversation, ...newMessages];

    try {
      const { data } = await API.post("/prompts/clarify-respond", {
        concept: concept.trim(),
        project_id: projectId,
        conversation: fullConversation,
      });
      setConversation(fullConversation);

      if (data.is_complete && data.enriched_concept) {
        setEnrichedConcept(data.enriched_concept);
        setFollowUps([]);
      } else if (data.follow_up && data.follow_up.length > 0) {
        setFollowUps(data.follow_up);
        // Clear previous follow-up answers
        const newAnswers = { ...answers };
        data.follow_up.forEach((q) => { newAnswers[q.id] = ""; });
        setAnswers(newAnswers);
      } else {
        // No follow-ups and not complete — force complete with what we have
        setEnrichedConcept(data.enriched_concept || concept);
      }
    } catch (e) {
      toast.error("Processing failed", { description: e.response?.data?.detail || "Try again." });
    } finally {
      setProcessing(false);
    }
  };

  const handleSkip = () => {
    onComplete(concept.trim(), null);
  };

  const handleAcceptEnriched = () => {
    onComplete(enrichedConcept, {
      original_concept: concept.trim(),
      analysis: analysis,
      conversation: conversation,
    });
  };

  const isAnalyzed = analysis !== null;
  const hasQuestions = questions.length > 0 || followUps.length > 0;
  const activeQuestions = followUps.length > 0 ? followUps : questions;

  return (
    <div className="max-w-3xl mx-auto space-y-6" data-testid="clarify-step">
      {/* Concept Input */}
      <div className="border border-[var(--pf-border)] bg-[var(--pf-bg)]">
        <div className="px-5 py-3 border-b border-[var(--pf-border)]">
          <h3 className="font-heading text-base font-bold text-[var(--pf-text)]">Your Concept</h3>
          <p className="text-xs text-[var(--pf-text-secondary)] mt-0.5">Describe your project idea. We'll analyze it and ask smart questions to strengthen your prompt.</p>
        </div>
        <div className="p-5">
          <Textarea
            value={concept}
            onChange={(e) => onConceptChange(e.target.value)}
            placeholder="Describe your SaaS idea, feature, or product concept in plain English..."
            data-testid="clarify-concept-input"
            className="rounded-none border-[var(--pf-border)] focus:ring-2 focus:ring-[var(--pf-accent)] resize-none font-mono text-sm min-h-[120px]"
            rows={5}
            disabled={analyzing}
          />
          <div className="flex items-center gap-2 mt-3">
            <Button
              onClick={handleAnalyze}
              disabled={analyzing || !concept.trim()}
              data-testid="clarify-analyze-button"
              className="bg-[var(--pf-accent)] hover:bg-[var(--pf-accent-hover)] text-white rounded-none h-9 px-4 text-xs font-medium"
            >
              {analyzing ? <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />Analyzing...</> : <><Sparkles className="w-3.5 h-3.5 mr-1.5" />Analyze Concept</>}
            </Button>
            <Button
              variant="ghost"
              onClick={handleSkip}
              disabled={analyzing || !concept.trim()}
              data-testid="clarify-skip-button"
              className="rounded-none h-9 px-4 text-xs text-[var(--pf-text-secondary)] hover:text-[var(--pf-text)]"
            >
              Skip to generation <ArrowRight className="w-3 h-3 ml-1" />
            </Button>
          </div>
        </div>
      </div>

      {/* Analysis Results */}
      {isAnalyzed && !enrichedConcept && (
        <div className="border border-[var(--pf-border)] bg-[var(--pf-bg)]" data-testid="clarify-analysis">
          <div className="px-5 py-3 border-b border-[var(--pf-border)]">
            <h3 className="font-heading text-base font-bold text-[var(--pf-text)]">Concept Analysis</h3>
          </div>
          <div className="p-5 grid grid-cols-2 gap-4">
            <div>
              <p className="text-[10px] uppercase tracking-[0.2em] font-semibold text-[#16A34A] mb-2">Strengths</p>
              <ul className="space-y-1.5">
                {(analysis.strengths || []).map((s, i) => (
                  <li key={i} className="flex items-start gap-2 text-xs text-[var(--pf-text)]">
                    <CheckCircle className="w-3.5 h-3.5 text-[#16A34A] mt-0.5 shrink-0" />
                    <span>{s}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-[0.2em] font-semibold text-[#EAB308] mb-2">Gaps to Address</p>
              <ul className="space-y-1.5">
                {(analysis.gaps || []).map((g, i) => (
                  <li key={i} className="flex items-start gap-2 text-xs text-[var(--pf-text)]">
                    <AlertCircle className="w-3.5 h-3.5 text-[#EAB308] mt-0.5 shrink-0" />
                    <span>{g}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}

      {/* Clarifying Questions */}
      {hasQuestions && !enrichedConcept && (
        <div className="border border-[var(--pf-border)] bg-[var(--pf-bg)]" data-testid="clarify-questions">
          <div className="px-5 py-3 border-b border-[var(--pf-border)]">
            <h3 className="font-heading text-base font-bold text-[var(--pf-text)]">
              {followUps.length > 0 ? "Follow-up Questions" : "Clarifying Questions"}
            </h3>
            <p className="text-xs text-[var(--pf-text-secondary)] mt-0.5">Answer what you can — even partial answers improve the output.</p>
          </div>
          <div className="p-5 space-y-4">
            {activeQuestions.map((q) => (
              <div key={q.id} className="border-l-2 border-[var(--pf-accent)] pl-4" data-testid={`clarify-q-${q.id}`}>
                <p className="text-sm font-medium text-[var(--pf-text)] mb-1">{q.question}</p>
                {q.why && <p className="text-[10px] text-[var(--pf-text-muted)] mb-2 italic">{q.why}</p>}
                <Textarea
                  value={answers[q.id] || ""}
                  onChange={(e) => setAnswers((prev) => ({ ...prev, [q.id]: e.target.value }))}
                  placeholder="Your answer..."
                  className="rounded-none border-[var(--pf-border)] focus:ring-2 focus:ring-[var(--pf-accent)] resize-none font-mono text-xs"
                  rows={2}
                  disabled={processing}
                />
              </div>
            ))}
            <div className="flex items-center gap-2 pt-2">
              <Button
                onClick={handleSubmitAnswers}
                disabled={processing || activeQuestions.every((q) => !answers[q.id]?.trim())}
                data-testid="clarify-submit-answers"
                className="bg-[var(--pf-accent)] hover:bg-[var(--pf-accent-hover)] text-white rounded-none h-9 px-4 text-xs font-medium"
              >
                {processing ? <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />Processing...</> : <><Send className="w-3.5 h-3.5 mr-1.5" />Submit Answers</>}
              </Button>
              <Button
                variant="ghost"
                onClick={handleSkip}
                disabled={processing}
                className="rounded-none h-9 px-4 text-xs text-[var(--pf-text-secondary)]"
              >
                Skip and use original concept
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Enriched Concept */}
      {enrichedConcept && (
        <div className="border border-[var(--pf-accent)] border-2 bg-[var(--pf-bg)]" data-testid="clarify-enriched">
          <div className="px-5 py-3 border-b border-[var(--pf-accent)] bg-[var(--pf-accent-subtle)]">
            <h3 className="font-heading text-base font-bold text-[var(--pf-accent)]">Enriched Concept</h3>
            <p className="text-xs text-[var(--pf-text-secondary)] mt-0.5">Your concept has been enhanced with your clarifications. This will be used for prompt generation.</p>
          </div>
          <div className="p-5">
            <div className="bg-[var(--pf-surface)] border border-[var(--pf-border)] p-4 font-mono text-sm leading-relaxed text-[var(--pf-text)] whitespace-pre-wrap max-h-[300px] overflow-y-auto">
              {enrichedConcept}
            </div>
            <div className="flex items-center gap-2 mt-4">
              <Button
                onClick={handleAcceptEnriched}
                data-testid="clarify-accept-enriched"
                className="bg-[var(--pf-accent)] hover:bg-[var(--pf-accent-hover)] text-white rounded-none h-10 px-5 text-sm font-medium"
              >
                <ArrowRight className="w-4 h-4 mr-1.5" />Accept & Generate Master Prompt
              </Button>
              <Button
                variant="outline"
                onClick={() => { setEnrichedConcept(""); setFollowUps([]); }}
                className="rounded-none border-[var(--pf-border)] h-10 text-sm"
              >
                Revise answers
              </Button>
            </div>
          </div>
        </div>
      )}
      <div ref={scrollRef} />
    </div>
  );
}
